import { useEffect, useMemo, useRef, useState, type FC } from "react";
import { Outlet } from "react-router-dom";

import { Button } from "../shared";
import { usePortalMe } from "../../queries/affiliate-portal/usePortalMe";
import { usePortalSignOut } from "../../queries/affiliate-portal/usePortalSignOut";
import {
  clearAffiliatePortalAndRedirect,
  decodeIdTokenClaims,
  getAffiliatePortalToken,
} from "../../utils/tokenUtils";
import { BRAND_ICONS_URLS } from "../../utils/Constants";
import { AppStrings } from "../../utils/constants/AppStrings";

import styles from "./AffiliatePortalLayout.module.css";

const BRAND_LOGO_SRC = `${process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL ?? ""}${BRAND_ICONS_URLS.topLeftAppIcon}`;

/**
 * Minimal layout for the external affiliate portal. Deliberately no PAM
 * sidebar / nav — affiliates only ever see this shell + the dashboard.
 *
 * Prefers data from `/affiliates-portal/me` for the header identity so we have
 * one source of truth across the portal. Falls back to the JWT email while the
 * /me query is in flight so the header doesn't flash blank on a cold render.
 */
const AffiliatePortalLayout: FC = () => {
  const { data: meData } = usePortalMe();
  const signOut = usePortalSignOut();
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const profileMenuRef = useRef<HTMLDivElement>(null);

  const fallbackEmail = useMemo(() => {
    const token = getAffiliatePortalToken();
    const claims = token?.IdToken ? decodeIdTokenClaims(token.IdToken) : null;
    return claims?.email ?? "";
  }, []);

  const headerLabel =
    meData?.profile.contact_name ||
    meData?.profile.company_name ||
    meData?.profile.email ||
    fallbackEmail;

  const userInitial = headerLabel?.charAt(0)?.toUpperCase() || "?";

  useEffect(() => {
    if (!isProfileMenuOpen) {
      return undefined;
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (!profileMenuRef.current?.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsProfileMenuOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isProfileMenuOpen]);

  const handleSignOutClicked = () => {
    signOut.mutate(undefined, {
      onSettled: () => {
        clearAffiliatePortalAndRedirect();
      },
    });
  };

  return (
    <div className={styles.shell} data-testid="affiliate-portal-layout">
      <header className={styles.header}>
        <div className={styles.brandRow}>
          <span className={styles.logo}>
            <img
              className={styles.logoImage}
              src={BRAND_LOGO_SRC}
              alt={AppStrings.REBET}
              width={22}
              height={22}
            />
          </span>
          <span className={styles.brand}>{AppStrings.AFFILIATE_PORTAL}</span>
        </div>
        <div className={styles.profileContainer} ref={profileMenuRef}>
          <button
            className={styles.profileTrigger}
            type="button"
            aria-label={AppStrings.OPEN_ACCOUNT_MENU}
            aria-expanded={isProfileMenuOpen}
            onClick={() => setIsProfileMenuOpen((previousValue) => !previousValue)}
          >
            <span className={styles.avatar} aria-hidden="true">
              <span className={styles.avatarText}>{userInitial}</span>
            </span>
            <span className={styles.profileEmail} data-testid="affiliate-portal-email">
              {headerLabel || AppStrings.UNKNOWN_USER}
            </span>
          </button>

          {isProfileMenuOpen ? (
            <div
              className={styles.popover}
              role="dialog"
              aria-label={AppStrings.ACCOUNT_MENU}
            >
              <div className={styles.popoverHeader}>
                <p className={styles.popoverEmail}>
                  {headerLabel || AppStrings.UNKNOWN_USER}
                </p>
              </div>

              <Button
                intent="primary"
                block
                className={styles.signoutButton}
                onClick={handleSignOutClicked}
                disabled={signOut.isPending}
                data-testid="affiliate-portal-logout"
              >
                {AppStrings.SIGN_OUT}
              </Button>
            </div>
          ) : null}
        </div>
      </header>
      <main className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};

export default AffiliatePortalLayout;
